import { Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { PaysafeForm, PaysafeFormField } from '@checkout/util-models';
import { AddressFormBuilder } from '@domgen/dgx-fe-forms/address';
import { FormControlValidators } from '@domgen/dgx-fe-forms/shared';
import { TranslateService } from '@domgen/dgx-fe-i18n';
import { FeatureCardTranslationDictionary } from '../../models/i18n/feature-card-translation-dictionary.interface';
import { PaysafeFormConfig } from '../../models/paysafe/paysafe-form-config.interface';

@Injectable()
export class PaysafeFormBuilder {
  constructor(
    private readonly addressFormBuilder: AddressFormBuilder,
    private readonly formBuilder: FormBuilder,
    private readonly translateService: TranslateService<FeatureCardTranslationDictionary>
  ) {}

  buildPaysafeForm(config: PaysafeFormConfig): FormGroup<PaysafeForm> {
    return this.formBuilder.group(this.buildPaysafeFormConfig(config));
  }

  private buildPaysafeFormConfig(config: PaysafeFormConfig): PaysafeForm {
    const { customerDetails } = config;

    return {
      [PaysafeFormField.Address]: this.addressFormBuilder.buildAddressForm(
        customerDetails?.address
      ),
      [PaysafeFormField.BillingAddress]:
        this.addressFormBuilder.buildAdditionalAddressForm(
          customerDetails?.address
        ),
      [PaysafeFormField.AccountName]: new FormControl(
        customerDetails
          ? `${customerDetails.firstName} ${customerDetails.surname}`
          : null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'CARD_FORM_ACCOUNT_NAME_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.alphabetical(
            this.translateService.translate(
              'CARD_FORM_ACCOUNT_NAME_ERROR_ALPHABETICAL'
            )
          ),
          FormControlValidators.minimumLength(
            2,
            this.translateService.translate(
              'CARD_FORM_ACCOUNT_NAME_ERROR_MIN_LENGTH',
              { minLength: 2 }
            )
          ),
          FormControlValidators.maximumLength(
            160,
            this.translateService.translate(
              'CARD_FORM_ACCOUNT_NAME_ERROR_MAX_LENGTH',
              { maxLength: 160 }
            )
          ),
        ])
      ),
      [PaysafeFormField.CardNumber]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'CARD_FORM_CARD_NUMBER_ERROR_REQUIRED'
            )
          ),
        ])
      ),
      [PaysafeFormField.Cvv]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate('CARD_FORM_CVV_ERROR_REQUIRED')
          ),
        ])
      ),
      [PaysafeFormField.ExpiryDate]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'CARD_FORM_EXPIRY_DATE_ERROR_REQUIRED'
            )
          ),
        ])
      ),
      [PaysafeFormField.MonthlyRenewal]: new FormControl(
        {
          value: false,
          disabled: !config.monthlyRenewalVisible,
        },
        Validators.compose([
          FormControlValidators.requiredTrue(
            this.translateService.translate(
              'CARD_FORM_MONTHLY_RENEWAL_ERROR_REQUIRED'
            )
          ),
        ])
      ),
      [PaysafeFormField.PaperlessPreference]: new FormControl(null),
      [PaysafeFormField.TermsAndConditions]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.requiredTrueConditionally(
            Boolean(config.termsAndConditionsFeatureEnabled),
            this.translateService.translate(
              'CARD_FORM_TERMS_AND_CONDITIONS_ERROR_REQUIRED'
            )
          ),
        ])
      ),
    };
  }
}
